import React, {
  Fragment,
  forwardRef,
  useMemo,
  useEffect,
  useState,
  useRef,
} from "react";
import useServiceStore from "store/serviceStore";
import { GRAINS, COMPASS, pixelPerInch } from "constants/appConstant";
import { HorizontalRuler, HospitalBed } from "components";
import Wrapper from "./headwall-components/wrapper";
import {
  alterImageUrl,
  calculateHeight,
  calculateWidth,
} from "config/calculations/calculation";

const Compass = forwardRef((props, ref) => {
  const {
    values,
    ruler,
    handleDragStart,
    handleonDragOver,
    handleDrop,
    handleDragEnter,
    dragEntered,
    service,
    clickArrowDown,
    clickArrowUp,
    clickArrowLeft,
    clickArrowRight,
    showBedGraphic
  } = props;
  const wrapperRef = useRef(null);

  const { serv, view_only, setService } = useServiceStore();
  const { headwalls, ceiling_height, model, accessory_rails } = values;

  const [railInd, setRailInd] = useState(0);
  const [rowInd, setRowInd] = useState(0);
  const [customEvent, setCustomEvent] = useState(null);

  const wallWidth = useMemo(() => {
    let totalWidth = 0;
    //added 10 inches extra to manage wall width
    if (headwalls.length >= 2) {
      headwalls.map((wall) => {
        totalWidth += Number(wall.width);
        return totalWidth;
      });
      return totalWidth / pixelPerInch + 700;
    }
  }, [headwalls]);

  const compassHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in - 9) / pixelPerInch //9 from bottom
    );
  }, [ceiling_height]);

  const wallHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch
    );
  }, [ceiling_height]);

  const rulerWidth = useMemo(() => {
    const totalInches = headwalls.reduce(
      (acc, curr) => acc + Number(curr.width),
      0
    );

    // Convert total inches to feet and inches
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return { ft: feet, in: inches };
  }, [headwalls]);

  const sectionWidth = (width) => {
    let secWidth = 0;
    switch (Number(width)) {
      case 12:
        secWidth = 12 / pixelPerInch;
        break;
      case 18:
        secWidth = 18 / pixelPerInch;
        break;
      case 24:
        secWidth = 24 / pixelPerInch;
        break;
      case 30:
        secWidth = 30 / pixelPerInch;
        break;
      case 36:
        secWidth = 36 / pixelPerInch;
        break;
      case 42:
        secWidth = 42 / pixelPerInch;
        break;
      case 48:
        secWidth = 48 / pixelPerInch;
        break;
      default:
        secWidth = 12 / pixelPerInch;
    }
    return secWidth;
  };

  const serviceWidth = (ser) => {
    return Math.round(ser.gang_width / pixelPerInch);
  };

  useEffect(() => {
    //to adjust scale for axiom
    // Select all elements with the class 'scale'
    if (ruler) {
      const firstElements = document.querySelector(".first");
      const nextElements = document.querySelectorAll(".next");

      // Update styles for 'first' elements
      firstElements.style.maxWidth = "214.285714285px";

      nextElements.forEach((element) => {
        element.style.maxWidth = "214.285714285px";
      });
      // Cleanup function to revert styles when component unmounts
      return () => {
        firstElements.style.maxWidth = "";
        nextElements.forEach((element) => {
          element.style.maxWidth = "";
        });
      };
    }
  }, [ruler, rulerWidth]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        // view_only && clickArrowUp(e, serv, railInd, rowInd, "compass");
        break;
      case "ArrowDown":
        // view_only && clickArrowDown(e, serv, railInd, rowInd, "compass");
        break;
      case "ArrowLeft":
        view_only && clickArrowLeft(e, serv, railInd, rowInd, "compass");
        break;
      case "ArrowRight":
        view_only && clickArrowRight(e, serv, railInd, rowInd, "compass");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let isDragging = false;

    const handleClickOutside = (e) => {
      const clickedInsideFlexItemsEnd = e.target.closest(
        ".flex.items-end".split(" ").join(".")
      );
      if (
        !isDragging &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        clickedInsideFlexItemsEnd == null
      ) {
        setService("", false);
      }
    };

    const handleDragStart = () => (isDragging = true);
    const handleDragEnd = () => (isDragging = false);

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside); // fixed
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside); // match above
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, [serv, view_only, railInd, rowInd]);

  const accessorieRailMargins = useMemo(() => {
    if (service?.service?.type === "surface_mounted")
      return "surfacemounted_rail_margins";
    else if (service?.service?.type === "embedded")
      return "embedded_rail_margins";
    else return "";
  }, [service?.service?.type]);

  const railBackgroundColor = useMemo(() => {
    if (
      service?.service?.type === "surface_mounted" ||
      service?.service?.type === "embedded"
    )
      return "bg-green-100";
    else return "";
  }, [service?.service?.type]);

  const accessoriePosition = (acc) => {
   const xPosition = acc.positions?.x;
  if (!xPosition || acc.mount_type !== "track") return xPosition;
    // 1. Adjustment for "Single (3 hook)"
  if (acc.name === "Single (3 hook)") {
    return xPosition - 20;
  }
  if (acc.name === "Dual (6 hook)") {
    return xPosition - 22;
  }
  if (acc.name === "Ambu Bag Holder") {
    return xPosition + 8;
  }

   if (acc.name === "Single") {
      return xPosition -6;
    }

    // 3. Default fallback for track-mounted accessories
  return xPosition;
  };

  return (
    <Wrapper
      ruler={ruler}
      width={wallWidth}
      wallHeight={wallHeight}
      ceiling_height={ceiling_height}
      model={model}
      compassHeight={compassHeight}
      ref={ref}
    >
      <div className="flex gap-[2px] pb-[19px] relative compass-board">
        {/* maping accessories rail data */}
        {Object.keys(accessory_rails)?.map((railKey, i) => {
          const railData = accessory_rails[railKey] || [];
          return (
            <>
              {railData?.map((ser, i) => (
                <div
                  draggable
                  key={i}
                  className="absolute flex justify-center z-10"
                  style={{
                    left: ser.positions?.x,
                    top: ser.positions?.y,
                    cursor: "grab",
                  }}
                  onDragStart={(e) => handleDragStart(ser, e)}
                  onDragEnter={(e) => handleDragEnter(e)}
                  onDrop={(e) => handleDrop(e, "rail")}
                  onDragOver={(e) => handleonDragOver(e, "rail")}
                >
                  {dragEntered &&
                    service?.service?.u_id !== ser?.u_id &&
                    service?.service?.is_accessory_rail &&
                    service?.service?.type !== "unimount" &&
                    ser.type !== "unimount" &&
                    ser.mount_type !== "direct" && (
                      <div
                        className="h-[234.815714286px] top-[-107.142857143px] non_dropable w-full absolute"
                        style={{ background: "rgb(215 176 162)", zIndex: 0 }}
                      />
                    )}
                  <div
                    draggable={"false"}
                    style={{
                      width: ser.width / pixelPerInch,
                      backgroundImage: `url(${ser.image_url})`,
                      backgroundSize:
                        ser.type === "unimount"
                          ? "contain"
                          : ser.width < 15
                          ? "cover"
                          : "contain",
                      backgroundPosition: "center",
                      pointerEvents: "none",
                      height: ser.height / pixelPerInch,
                      zIndex: 1,
                    }}
                    onDragEnter={(e) => e.stopPropagation()}
                  />
                </div>
              ))}
            </>
          );
        })}
        {/* maping accessories */}
        {Object.keys(accessory_rails)?.map((railKey, i) => {
          const railData = accessory_rails[railKey] || [];
          return (
            <>
              {railData?.map((ser, i) => (
                <>
                  {ser.accessories?.map((acc, i) => (
                    <div
                      draggable
                      key={i}
                      className="absolute flex justify-center z-10"
                      style={{
                        left: accessoriePosition(acc),
                        top: acc.positions?.y,
                        cursor: "grab",
                      }}
                      onDragStart={(e) => handleDragStart(acc, e, 1)} //sending 1 to manage that accessory rail is dragged from compass board
                      onDragEnter={(e) => handleDragEnter(e)}
                      onDrop={(e) => handleDrop(e, "rail")}
                      onDragOver={(e) => handleonDragOver(e, "rail")}
                    >
                      <div
                        draggable={"false"}
                        style={{
                          width: calculateWidth(acc),
                          backgroundImage: `url(${acc.image})`,
                          backgroundSize: "contain",
                          backgroundRepeat:"no-repeat",
                          backgroundPosition: "center",
                          pointerEvents: "none",
                          height: calculateHeight(acc),
                          zIndex: 1,
                        }}
                        onDragEnter={(e) => e.stopPropagation()}
                      />
                    </div>
                  ))}
                </>
              ))}
            </>
          );
        })}
        {/* maping direct accessories */}
        {accessory_rails["direct"]?.map((acc, i) => (
          <div
            draggable
            key={i}
            className="absolute flex justify-center z-10"
            style={{
              left: acc.positions?.x,
              top: acc.positions?.y,
              cursor: "grab",
            }}
            onDragStart={(e) => handleDragStart(acc, e, 1)} //sending 1 to manage that accessory rail is dragged from compass board
            onDragEnter={(e) => handleDragEnter(e)}
            onDrop={(e) => handleDrop(e, "rail")}
            onDragOver={(e) => handleonDragOver(e, "rail")}
          >
            <div
              draggable={"false"}
              style={{
                width: acc.width / pixelPerInch,
                backgroundImage: `url(${acc.image})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                pointerEvents: "none",
                height: acc.height / pixelPerInch,
              }}
              onDragEnter={(e) => e.stopPropagation()}
            />
          </div>
        ))}
        {headwalls.map((rail, railIndex) => (
          <Fragment key={railIndex}>
            <div>
              <div
                // subtracting 2 to manage 2px gap for compass
                style={{ width: sectionWidth(rail.width) - 2 }}
                className="flex flex-col gap-[2px] relative"
                id={`compass_section_${railIndex}`}
                onDragEnter={(e) => handleDragEnter(e, railIndex)}
                onDrop={(e) => handleDrop(e, "rail", railIndex)}
              >
                {rail.services
                  .filter((serv) => serv.is_slide)
                  .map((ser, serviceIndex) => (
                    <div
                      draggable
                      key={serviceIndex}
                      className={`absolute flex justify-center z-10 ${
                        dragEntered ? "non_dropable " : ""
                      }`}
                      style={{
                        left: ser.positions?.x,
                        top: ser.positions?.y,
                        cursor: "grab",
                        background:
                          ser == serv
                            ? view_only
                              ? "#75060f"
                              : "transparent"
                            : "transparent",
                      }}
                      onDragStart={(e) =>
                        handleDragStart(ser, e, railIndex, serviceIndex)
                      }
                      onDragEnter={(e) => handleDragEnter(e, railIndex)}
                      onClick={() => {
                        setService(ser, true);

                        setRailInd(railIndex);
                        setRowInd(serviceIndex);
                      }}
                    >
                      <div
                        draggable={"false"}
                        style={{
                          width: serviceWidth(ser),
                          backgroundImage: `url(${alterImageUrl(
                            ser.image_url
                          )})`,
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          pointerEvents: "none",
                          height: ser.height / pixelPerInch,
                        }}
                        onDragEnter={(e) => e.stopPropagation()}
                      />
                    </div>
                  ))}
                {/* section 1 */}
                <div
                  className={`w-full compass-section relative`}
                  style={{
                    background: rail?.color,
                    height: `${COMPASS.SECTION_1_HEIGHT}px`,
                    backgroundImage: `url(${GRAINS[rail?.background]})`,
                  }}
                  onDragEnter={(e) => handleDragEnter(e, railIndex)}
                  onDragOver={(e) => handleonDragOver(e, "rail")}
                  onDrop={(e) => handleDrop(e, "rail", railIndex)}
                >
                  {/*----- to highlight droppable areas for accessorie rails------ */}
                  <div
                    className={`absolute ${
                      dragEntered && railBackgroundColor
                    } ${dragEntered && accessorieRailMargins} `}
                  />
                  {/* -------------------------------------------------------------
                   */}
                  {/* Row 1 */}
                  <Row
                    row={1}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px]  mt-[20.1785714286px]"}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />

                  {/* Row 2 */}
                  <Row
                    row={2}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px] my-[19.8214285714px]"}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />
                </div>
                {/* section 2 */}
                <div
                  className={`w-full compass-section relative`}
                  style={{
                    background: rail?.color,
                    height: `${COMPASS.SECTION_2_HEIGHT - 2}px`, //-2 to adjust gap for above 1 section
                    backgroundImage: `url(${GRAINS[rail?.background]})`,
                  }}
                  onDragEnter={(e) => handleDragEnter(e, railIndex)}
                  onDragOver={(e) => handleonDragOver(e, "rail")}
                  onDrop={(e) => handleDrop(e, "rail", railIndex)}
                >
                  {/*----- to highlight droppable areas for accessorie rails------ */}
                  <div
                    className={`absolute ${
                      dragEntered && railBackgroundColor
                    } ${dragEntered && accessorieRailMargins} `}
                  />
                  {/* ------------------------------------------------------------- */}
                  {/* Row 3 */}
                  <Row
                    row={3}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px] mt-[16.9642857143px]"}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />

                  {/* Row 4 */}
                  <Row
                    row={4}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px] my-[19.8214285714px]"}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />

                  {/* Row 5 */}
                  <Row
                    row={5}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px] "}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />
                </div>
                {/* section 3 */}
                <div
                  className={`w-full compass-section relative `}
                  style={{
                    background: rail?.color,
                    height: `${COMPASS.SECTION_3_HEIGHT - 4}px`, //-4 to adjust 4 px gap for above 2 section
                    backgroundImage: `url(${GRAINS[rail?.background]})`,
                  }}
                  onDragEnter={(e) => handleDragEnter(e, railIndex)}
                  onDragOver={(e) => handleonDragOver(e, "rail")}
                  onDrop={(e) => handleDrop(e, "rail", railIndex)}
                >
                  {/*----- to highlight droppable areas for accessorie rails------ */}
                  <div
                    className={`absolute ${
                      dragEntered && railBackgroundColor
                    } ${dragEntered && accessorieRailMargins} `}
                  />
                  {/* ------------------------------------------------------------ */}
                  {/* Row 6 */}
                  <Row
                    row={6}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px]  mx-[49px] mt-[27px]"}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />
                  {/* Row 7 */}
                  <Row
                    row={7}
                    rail={rail}
                    railIndex={railIndex}
                    dragEntered={dragEntered}
                    className={"h-[94.4642857143px]  mx-[49px] mt-[115px]"}
                    handleonDragOver={handleonDragOver}
                    handleDrop={handleDrop}
                    handleDragEnter={handleDragEnter}
                    handleDragStart={handleDragStart}
                    service={service}
                    clickArrowLeft={clickArrowLeft}
                    clickArrowRight={clickArrowRight}
                    rowInd={setRowInd}
                    railInd={setRailInd}
                    customEvent={setCustomEvent}
                    wrapperRef={wrapperRef}
                  />
                </div>
              </div>
              <p className="flex justify-center mt-2 txtBlue font-semibold text-base">
                Section {railIndex + 1}
              </p>
            </div>
          </Fragment>
        ))}
      </div>
      {ruler && (
        <HorizontalRuler
          class_name={`bottom-[17px] mt-10 z-10`}
          width={rulerWidth}
        />
      )}
       {showBedGraphic && <HospitalBed />}
    </Wrapper>
  );
});

export default Compass;

const Row = ({
  row,
  rail,
  railIndex,
  dragEntered,
  className,
  handleonDragOver,
  handleDrop,
  handleDragEnter,
  handleDragStart,
  clickArrowLeft,
  clickArrowRight,
  service,
  customEvent,
  railInd,
  rowInd,
  wrapperRef,
}) => {
  const { serv, view_only, setService } = useServiceStore();
  const serviceWidth = (ser) => {
    return ser.gang_width / pixelPerInch;
  };

  const railBackgroundColor = useMemo(() => {
    if (
      service?.service?.type === "surface_mounted" ||
      service?.service?.type === "embedded" ||
      service?.service?.type === "unimount" ||
      service?.service?.is_accessory
    )
      return "";
    else return "bg-green-100";
  }, [service?.service?.type, service?.service?.is_accessory]);

  return (
    <>
      <div
        className={`relative flex items-center ${className} ${
          dragEntered && railBackgroundColor
        }`}
        onDragEnter={(e) => handleDragEnter(e, railIndex)}
        onDragOver={(e) => handleonDragOver(e, "rail")}
        onDrop={(e) => handleDrop(e, "rail", railIndex, row)}
        style={{
          marginInline: "auto",
          maxWidth: (rail.width - 5.5) / pixelPerInch,
        }}
      >
        {rail.services
          .filter(
            (serv) =>
              serv.rowPosition === row &&
              serv.category !== "Vacuum Bottle Slide"
          )
          .map((ser, serviceIndex) => (
            <div
              ref={wrapperRef}
              draggable
              key={serviceIndex}
              className={`absolute flex justify-center 
                        ${
                          dragEntered &&
                          !service?.service?.is_accessory &&
                          !service?.service?.is_accessory_rail
                            ? "non_dropable "
                            : dragEntered && "z-10"
                        }`}
              // ${dragEntered ? "non_dropable " : ""}`}
              style={{
                top: "0px",
                left: ser.positions?.x,
                cursor: "grab",
                background:
                  ser == serv
                    ? view_only
                      ? "#75060f"
                      : "transparent"
                    : "transparent",
              }}
              onDragStart={(e) =>
                handleDragStart(ser, e, railIndex, serviceIndex, row)
              }
              onDragEnter={(e) => e.stopPropagation()}
              onClick={() => {
                // const rowBlock = document.getElementById(
                //   `compass_section_${railIndex}`
                // );
                // const fakeEvent = {
                //   target: rowBlock,
                //   currentTarget: rowBlock,
                //   stopPropagation: () => {
                //     // No-op or logging for debug
                //     console.log("stopPropagation() called");
                //   },
                // };
                // customEvent(fakeEvent);
                setService(ser, true);
                railInd(railIndex);
                rowInd(row);
              }}
            >
              <div
                draggable={"false"}
                className="pointer-events-none"
                style={{
                  width: serviceWidth(ser),
                  backgroundImage: `url(${alterImageUrl(ser.image_url)})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  pointerEvents: "none",
                  height: ser.height / pixelPerInch,
                }}
                onDragEnter={(e) => e.stopPropagation()}
              />
            </div>
          ))}
      </div>
    </>
  );
};
